import { Skeleton } from '@mantine/core';
import { isNil } from 'lodash';
import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { OutputValueType } from '../../utils/calculated-value';
import { TypographyVariant } from '../../utils/theme';
import { getFormattedCalculatedValue } from '../../utils/calculated-value';
import Typography from '../typography/typography';

interface Value {
  label: string;
  value?: number | string;
  outputValueType: OutputValueType;
  outputValueSymbol?: string;
}

export interface ICalculatedValue {
  title?: string;
  description?: string;
  isLoading?: boolean;
  values?: Value[];
}

const CalculatedValue = ({
  title,
  description,
  isLoading,
  values,
}: ICalculatedValue) => {
  const theme = useTheme();
  return (
    <Wrapper data-testid="calculated-value">
      {title && <Typography variant={TypographyVariant.h3}>{title}</Typography>}
      <Container>
        {description && (
          <Typography variant={TypographyVariant.bodyMd1}>
            {description}
          </Typography>
        )}
        {values?.map((calculatedValue) => {
          const { label, value, outputValueType, outputValueSymbol } =
            calculatedValue;
          return (
            <Contribution key={label}>
              <Typography
                variant={TypographyVariant.bodySm2}
                color={theme.palette.neutral[90]}
              >
                {label}
              </Typography>
              {isLoading ? (
                <Skeleton height={20} width={100} />
              ) : (
                <Typography variant={TypographyVariant.bodyMd1}>
                  {isNil(value)
                    ? '-'
                    : getFormattedCalculatedValue(
                        outputValueType,
                        outputValueSymbol,
                        value
                      )}
                </Typography>
              )}
            </Contribution>
          );
        })}
      </Container>
    </Wrapper>
  );
};

export default memo(CalculatedValue);

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2.5)};
    justify-content: flex-start;
  `}
`;

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2.5)};
    justify-content: flex-start;
    border-radius: ${theme.borderRadius.sm};
    border: 1px solid ${theme.palette.neutral[60]};
    background: ${theme.palette.neutral[40]};
    padding: ${theme.spacing(2.5, 2.5, 3)};
  `}
`;

const Contribution = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;
